import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "p-4 sm:p-6" }
const _hoisted_2 = { class: "mb-6 sm:mb-8" }
const _hoisted_3 = { class: "relative overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 dark:from-blue-600 dark:via-blue-700 dark:to-indigo-800 rounded-2xl shadow-xl" }
const _hoisted_4 = { class: "relative px-6 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12" }
const _hoisted_5 = { class: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" }
const _hoisted_6 = { class: "flex-1" }
const _hoisted_7 = { class: "flex items-center gap-3 mb-2" }
const _hoisted_8 = { class: "text-2xl sm:text-3xl md:text-4xl font-bold text-white" }
const _hoisted_9 = { class: "text-blue-100 text-sm sm:text-base mt-1" }
const _hoisted_10 = { class: "mt-6 pt-6 border-t border-white/20" }
const _hoisted_11 = { class: "grid grid-cols-2 sm:grid-cols-3 gap-4" }
const _hoisted_12 = { class: "flex items-center gap-3" }
const _hoisted_13 = { class: "text-white font-semibold text-sm" }
const _hoisted_14 = { class: "flex items-center gap-3" }
const _hoisted_15 = { class: "text-white font-semibold text-sm" }
const _hoisted_16 = { class: "flex items-center gap-3 mb-4" }
const _hoisted_17 = { class: "text-xl font-bold text-gray-800 dark:text-white" }
const _hoisted_18 = { class: "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6" }
const _hoisted_19 = { class: "border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full" }
const _hoisted_20 = {
  key: 0,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_21 = {
  key: 1,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_22 = { class: "flex items-start justify-between mb-12" }
const _hoisted_23 = { class: "flex-1" }
const _hoisted_24 = { class: "mt-auto" }
const _hoisted_25 = { class: "border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full" }
const _hoisted_26 = {
  key: 0,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_27 = {
  key: 1,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_28 = { class: "flex items-start justify-between mb-12" }
const _hoisted_29 = { class: "flex-1" }
const _hoisted_30 = { class: "mt-auto" }
const _hoisted_31 = { class: "border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full" }
const _hoisted_32 = {
  key: 0,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_33 = {
  key: 1,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_34 = { class: "flex items-start justify-between mb-12" }
const _hoisted_35 = { class: "flex-1" }
const _hoisted_36 = { class: "mt-auto" }
const _hoisted_37 = { class: "border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-xl shadow-lg h-full" }
const _hoisted_38 = {
  key: 0,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_39 = {
  key: 1,
  class: "p-6 h-full flex flex-col"
}
const _hoisted_40 = { class: "flex items-start justify-between mb-12" }
const _hoisted_41 = { class: "flex-1" }
const _hoisted_42 = { class: "mt-auto" }

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useBrand } from '@/composables/brand/useBrand'
import { useAuthStore } from '@/stores/auth'
import { adminTireService, type BrandStats } from '@/services/api/adminTireService'
import { getBrandById } from '@/config/brands'

// Composables

export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardView',
  setup(__props) {

const { brandColors } = useBrand()
const authStore = useAuthStore()

// State
const loading = ref(false)
const brandStats = ref<BrandStats[]>([])
const currentTime = ref('')
const currentDate = ref('')

// Computed
const adminName = computed(() => {
  const user = authStore.currentUser
  if (user?.last_name) {
    return user.last_name
  }
  return 'Admin'
})

const currentGreeting = computed(() => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning! Ready to manage your dashboard.'
  if (hour < 18) return 'Good afternoon! Here\'s your overview for today.'
  return 'Good evening! Check out today\'s summary.'
})

// Update time and date
const updateDateTime = () => {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
  currentDate.value = now.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

let timeInterval: number | undefined

// Helper functions for brand-specific colors
const getBrandColor = (brandId: number) => {
  const brand = getBrandById(brandId)
  return brand?.color || brandColors.value.primary
}

const getBrandAccent = (brandId: number) => {
  const brand = getBrandById(brandId)
  return brand?.accentColor || brandColors.value.accent
}

// Methods
async function loadDashboardStats() {
  loading.value = true
  try {
    const response = await adminTireService.getDashboardStats()
    brandStats.value = response.data
  } catch (error) {
    console.error('Error loading dashboard stats:', error)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadDashboardStats()
  updateDateTime()
  // Update time every second
  timeInterval = window.setInterval(updateDateTime, 1000)
})

onUnmounted(() => {
  if (timeInterval) {
    clearInterval(timeInterval)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[6] || (_cache[6] = _createStaticVNode("<div class=\"absolute inset-0 opacity-10\" data-v-7ad47765><svg class=\"w-full h-full\" xmlns=\"http://www.w3.org/2000/svg\" data-v-7ad47765><defs data-v-7ad47765><pattern id=\"grid\" width=\"40\" height=\"40\" patternUnits=\"userSpaceOnUse\" data-v-7ad47765><circle cx=\"20\" cy=\"20\" r=\"1.5\" fill=\"white\" data-v-7ad47765></circle></pattern></defs><rect width=\"100%\" height=\"100%\" fill=\"url(#grid)\" data-v-7ad47765></rect></svg></div>", 1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", null, [
                  _createElementVNode("h1", _hoisted_8, " Welcome back, " + _toDisplayString(adminName.value) + "! 👋 ", 1),
                  _createElementVNode("p", _hoisted_9, _toDisplayString(currentGreeting.value), 1)
                ])
              ])
            ]),
            _cache[0] || (_cache[0] = _createStaticVNode("<div class=\"flex-shrink-0\" data-v-7ad47765><div class=\"inline-flex items-center gap-2 px-4 py-2.5 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30\" data-v-7ad47765><div class=\"flex items-center gap-2\" data-v-7ad47765><div class=\"w-2 h-2 bg-green-400 rounded-full animate-pulse\" data-v-7ad47765></div><span class=\"text-white text-sm font-medium\" data-v-7ad47765>System Active</span></div></div></div>", 1))
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                _cache[2] || (_cache[2] = _createElementVNode("div", { class: "flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center" }, [
                  _createElementVNode("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    viewBox: "0 0 24 24",
                    fill: "currentColor",
                    class: "w-5 h-5 text-white"
                  }, [
                    _createElementVNode("path", { d: "M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" }),
                    _createElementVNode("path", {
                      "fill-rule": "evenodd",
                      d: "M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z",
                      "clip-rule": "evenodd"
                    })
                  ])
                ], -1)),
                _createElementVNode("div", null, [
                  _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-blue-100 text-xs" }, "Today", -1)),
                  _createElementVNode("p", _hoisted_13, _toDisplayString(currentDate.value), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center" }, [
                  _createElementVNode("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    viewBox: "0 0 24 24",
                    fill: "currentColor",
                    class: "w-5 h-5 text-white"
                  }, [
                    _createElementVNode("path", {
                      "fill-rule": "evenodd",
                      d: "M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z",
                      "clip-rule": "evenodd"
                    })
                  ])
                ], -1)),
                _createElementVNode("div", null, [
                  _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-blue-100 text-xs" }, "Time", -1)),
                  _createElementVNode("p", _hoisted_15, _toDisplayString(currentTime.value), 1)
                ])
              ]),
              _cache[5] || (_cache[5] = _createStaticVNode("<div class=\"flex items-center gap-3 col-span-2 sm:col-span-1\" data-v-7ad47765><div class=\"flex-shrink-0 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center\" data-v-7ad47765><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"currentColor\" class=\"w-5 h-5 text-white\" data-v-7ad47765><path fill-rule=\"evenodd\" d=\"M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z\" clip-rule=\"evenodd\" data-v-7ad47765></path></svg></div><div data-v-7ad47765><p class=\"text-blue-100 text-xs\" data-v-7ad47765>Role</p><p class=\"text-white font-semibold text-sm\" data-v-7ad47765>Administrator</p></div></div>", 1))
            ])
          ])
        ])
      ])
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(brandStats.value, (brand) => {
      return (_openBlock(), _createElementBlock("div", {
        key: brand.brand_id,
        class: "mb-8"
      }, [
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("h2", _hoisted_17, _toDisplayString(brand.brand_name), 1)
        ]),
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("div", _hoisted_19, [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[7] || (_cache[7] = [
                  _createStaticVNode("<div class=\"flex items-start justify-between mb-12\" data-v-7ad47765><div class=\"flex-1 space-y-3\" data-v-7ad47765><div class=\"h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div><div class=\"h-3 w-40 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div><div class=\"w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse\" data-v-7ad47765></div></div><div class=\"mt-auto\" data-v-7ad47765><div class=\"h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div>", 2)
                ])))
              : (_openBlock(), _createElementBlock("div", _hoisted_21, [
                  _createElementVNode("div", _hoisted_22, [
                    _createElementVNode("div", _hoisted_23, [
                      _createElementVNode("p", {
                        class: "text-sm font-semibold uppercase tracking-wide mb-1",
                        style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                      }, " Total Customers ", 4),
                      _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400 mb-3" }, " All registered customers ", -1))
                    ]),
                    _createElementVNode("div", {
                      class: "p-3 rounded-xl flex-shrink-0",
                      style: _normalizeStyle({ backgroundColor: getBrandAccent(brand.brand_id) + '20' })
                    }, [
                      (_openBlock(), _createElementBlock("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 24 24",
                        fill: "currentColor",
                        class: "size-6",
                        style: _normalizeStyle({ color: getBrandAccent(brand.brand_id) })
                      }, _cache[9] || (_cache[9] = [
                        _createElementVNode("path", { d: "M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" }, null, -1)
                      ]), 4))
                    ], 4)
                  ]),
                  _createElementVNode("div", _hoisted_24, [
                    _createElementVNode("p", {
                      class: "text-4xl font-medium dark:text-white",
                      style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                    }, _toDisplayString(brand.total_customers), 5)
                  ])
                ]))
          ]),
          _createElementVNode("div", _hoisted_25, [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_26, _cache[10] || (_cache[10] = [
                  _createStaticVNode("<div class=\"flex items-start justify-between mb-12\" data-v-7ad47765><div class=\"flex-1 space-y-3\" data-v-7ad47765><div class=\"h-4 w-36 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div><div class=\"h-3 w-44 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div><div class=\"w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse\" data-v-7ad47765></div></div><div class=\"mt-auto\" data-v-7ad47765><div class=\"h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div>", 2)
                ])))
              : (_openBlock(), _createElementBlock("div", _hoisted_27, [
                  _createElementVNode("div", _hoisted_28, [
                    _createElementVNode("div", _hoisted_29, [
                      _createElementVNode("p", {
                        class: "text-sm font-semibold uppercase tracking-wide mb-1",
                        style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                      }, " Pending Tires ", 4),
                      _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400 mb-3" }, " Tires awaiting approval ", -1))
                    ]),
                    _createElementVNode("div", {
                      class: "p-3 rounded-xl flex-shrink-0",
                      style: _normalizeStyle({ backgroundColor: getBrandAccent(brand.brand_id) + '20' })
                    }, [
                      (_openBlock(), _createElementBlock("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 24 24",
                        fill: "currentColor",
                        class: "size-6",
                        style: _normalizeStyle({ color: getBrandAccent(brand.brand_id) })
                      }, _cache[12] || (_cache[12] = [
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          d: "M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z",
                          "clip-rule": "evenodd"
                        }, null, -1)
                      ]), 4))
                    ], 4)
                  ]),
                  _createElementVNode("div", _hoisted_30, [
                    _createElementVNode("p", {
                      class: "text-4xl font-medium dark:text-white",
                      style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                    }, _toDisplayString(brand.pending_tires), 5)
                  ])
                ]))
          ]),
          _createElementVNode("div", _hoisted_31, [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_32, _cache[13] || (_cache[13] = [
                  _createStaticVNode("<div class=\"flex items-start justify-between mb-12\" data-v-7ad47765><div class=\"flex-1 space-y-3\" data-v-7ad47765><div class=\"h-4 w-36 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div><div class=\"h-3 w-40 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div><div class=\"w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse\" data-v-7ad47765></div></div><div class=\"mt-auto\" data-v-7ad47765><div class=\"h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div>", 2)
                ])))
              : (_openBlock(), _createElementBlock("div", _hoisted_33, [
                  _createElementVNode("div", _hoisted_34, [
                    _createElementVNode("div", _hoisted_35, [
                      _createElementVNode("p", {
                        class: "text-sm font-semibold uppercase tracking-wide mb-1",
                        style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                      }, " Approved Tires ", 4),
                      _cache[14] || (_cache[14] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400 mb-3" }, " Tires that are approved ", -1))
                    ]),
                    _createElementVNode("div", {
                      class: "p-3 rounded-xl flex-shrink-0",
                      style: _normalizeStyle({ backgroundColor: getBrandAccent(brand.brand_id) + '20' })
                    }, [
                      (_openBlock(), _createElementBlock("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 24 24",
                        fill: "currentColor",
                        class: "size-6",
                        style: _normalizeStyle({ color: getBrandAccent(brand.brand_id) })
                      }, _cache[15] || (_cache[15] = [
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          d: "M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z",
                          "clip-rule": "evenodd"
                        }, null, -1)
                      ]), 4))
                    ], 4)
                  ]),
                  _createElementVNode("div", _hoisted_36, [
                    _createElementVNode("p", {
                      class: "text-4xl font-medium dark:text-white",
                      style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                    }, _toDisplayString(brand.approved_tires), 5)
                  ])
                ]))
          ]),
          _createElementVNode("div", _hoisted_37, [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_38, _cache[16] || (_cache[16] = [
                  _createStaticVNode("<div class=\"flex items-start justify-between mb-12\" data-v-7ad47765><div class=\"flex-1 space-y-3\" data-v-7ad47765><div class=\"h-4 w-32 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div><div class=\"h-3 w-44 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div><div class=\"w-14 h-14 bg-gray-200 dark:bg-gray-800 rounded-xl skeleton-pulse\" data-v-7ad47765></div></div><div class=\"mt-auto\" data-v-7ad47765><div class=\"h-10 w-20 bg-gray-200 dark:bg-gray-800 rounded skeleton-pulse\" data-v-7ad47765></div></div>", 2)
                ])))
              : (_openBlock(), _createElementBlock("div", _hoisted_39, [
                  _createElementVNode("div", _hoisted_40, [
                    _createElementVNode("div", _hoisted_41, [
                      _createElementVNode("p", {
                        class: "text-sm font-semibold uppercase tracking-wide mb-1",
                        style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                      }, " Pending Claims ", 4),
                      _cache[17] || (_cache[17] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400 mb-3" }, " Claims requiring review ", -1))
                    ]),
                    _createElementVNode("div", {
                      class: "p-3 rounded-xl flex-shrink-0",
                      style: _normalizeStyle({ backgroundColor: getBrandAccent(brand.brand_id) + '20' })
                    }, [
                      (_openBlock(), _createElementBlock("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        viewBox: "0 0 24 24",
                        fill: "currentColor",
                        class: "size-6",
                        style: _normalizeStyle({ color: getBrandAccent(brand.brand_id) })
                      }, _cache[18] || (_cache[18] = [
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          d: "M7.502 6h7.128A3.375 3.375 0 0 1 18 9.375v9.375a3 3 0 0 0 3-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 0 0-.673-.05A3 3 0 0 0 15 1.5h-1.5a3 3 0 0 0-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6ZM13.5 3A1.5 1.5 0 0 0 12 4.5h4.5A1.5 1.5 0 0 0 15 3h-1.5Z",
                          "clip-rule": "evenodd"
                        }, null, -1),
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          d: "M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 0 1 3 20.625V9.375ZM6 12a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V12Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75ZM6 15a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V15Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75ZM6 18a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V18Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75Z",
                          "clip-rule": "evenodd"
                        }, null, -1)
                      ]), 4))
                    ], 4)
                  ]),
                  _createElementVNode("div", _hoisted_42, [
                    _createElementVNode("p", {
                      class: "text-4xl font-medium dark:text-white",
                      style: _normalizeStyle({ color: getBrandColor(brand.brand_id) })
                    }, _toDisplayString(brand.pending_claims), 5)
                  ])
                ]))
          ])
        ])
      ]))
    }), 128))
  ]))
}
}

})